import { IVendorVariantInternal } from '@models/VendorVariant/i-vendor-variant';
import env from '@lib/envs/env';
import buildMerchGroupCategoryBreadcrumbs from '@lib/merchGroup/buildMerchGroupCategoryBreadcrumbs';

export default function buildJsonSchema(
  vendorVariant: IVendorVariantInternal,
  vanityPath: string,
  nameOverride?: string,
) {
  return [{
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: nameOverride || vendorVariant.title,
    description: vendorVariant.description,
    brand: vendorVariant.vendor.name,
    image: vendorVariant.images[0]?.uri,
    aggregateRating: getAggregateRating(vendorVariant),
    category: vendorVariant.taxonomy?.title,
    color: vendorVariant.color,
    material: vendorVariant.material,
    size: vendorVariant.dimensions,
    sku: vendorVariant.sku,
    url: `https://${env.DOMAIN_NAME}${vanityPath}`,
    offers: {
      '@type': 'Offer',
      url: `https://${env.DOMAIN_NAME}${vanityPath}`,
      priceCurrency: 'USD',
      price: vendorVariant.retailPrice?.price,
      itemCondition: 'https://schema.org/NewCondition',
      availability: getAvailability(vendorVariant),
    },
    custom: {
      alternateImages: vendorVariant.images.length > 1 ?
        vendorVariant.images.slice(1).map((image) => image.uri) : [],
      collectionName: vendorVariant.vendorProductGroup?.collectionName ?? undefined,
      vendorId: vendorVariant.vendor.id,
      brandId: vendorVariant.brandId
    },
    inProductGroupWithID: vendorVariant.vendorProductGroup?.vendorProductGroupName ?? null,
    review: vendorVariant.reviews?.items ?
      vendorVariant.reviews.items.map((review) => getReview(review)) :
      [],
  },
  getBreadcrumb(vendorVariant)];
}

function getAggregateRating(vendorVariant: IVendorVariantInternal) {
  if (
    !vendorVariant.reviewStats
    || !vendorVariant.reviewStats.average
    || !vendorVariant.reviewStats.count
    || vendorVariant.reviewStats.count < 0
  ) return null;

  return {
    '@type': 'AggregateRating',
    ratingValue: vendorVariant.reviewStats?.average,
    reviewCount: vendorVariant.reviewStats?.count,
  };
}

function getAvailability(vendorVariant: IVendorVariantInternal) {
  if (!vendorVariant.availability) return null;

  return vendorVariant.availability.includes('in_stock')
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';
}

interface IBreadcrumbScheme {
  '@type': string;
  position: number;
  name: string;
  item: string;
}

function getBreadcrumb(vendorVariant: IVendorVariantInternal) {
  const baseUrl = 'https://havenly.com';
  const itemListElement: IBreadcrumbScheme[] = [{
    '@type': 'ListItem',
    position: 1,
    name: 'Shop',
    item: `${baseUrl}/shop`
  }];

  const { publicBrandUrl, name: vendorName } = vendorVariant.vendor || {};

  if (publicBrandUrl && vendorName) {
    itemListElement.push({
      '@type': 'ListItem',
      position: 2,
      name: vendorName,
      item: publicBrandUrl
    });
  }

  const merchGroupCategoryBreadcrumbs = buildMerchGroupCategoryBreadcrumbs(
    vendorVariant?.merchGroupTaxonomy
  );

  itemListElement.push(...merchGroupCategoryBreadcrumbs.map((tcbc, idx) => ({
    '@type': 'ListItem',
    position: itemListElement.length + idx + 1,
    name: tcbc.title,
    item: `${baseUrl}/${tcbc.slug}`
  })));

  return {
    '@context': 'https://schema.org/',
    '@type': 'BreadcrumbList',
    itemListElement,
  };
}

function getReview(review: {
  description: string;
  ratingOverall: number;
  user: {
    name: string;
  };
}) {
  if (!review) return null;

  return {
    '@type': 'Review',
    reviewRating: {
      '@type': 'Rating',
      ratingValue: review.ratingOverall,
      bestRating: 5,
    },
    author: review.user?.name,
    description: review.description,
  };
}
